import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import {
    faCheck,
    faCircleInfo,
    faExclamationTriangle,
    faGlobe,
    faLink,
    faPlus,
    faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getEngageDomains } from 'api/engage';
import { DNSRecordTable } from 'assets/js/pages/engage/components/DomainSettings/DNSRecordTable';
import { DNSVerification } from 'assets/js/pages/engage/components/DomainSettings/DNSVerification';
import { DeleteDomainDialog } from 'assets/js/pages/engage/components/DomainSettings/DeleteDomainDialog';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Tooltip from 'components/tooltip';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { transformEngageDomainQuery } from 'helpers/reactQuery';
import { accountQueryKeys } from 'queries/account-query-keys';

const DomainSettings = () => {
    const { accountId } = useCurrentAccount();
    const [isDeletingCustomDomain, setIsDeletingCustomDomain] = useState(false);
    const [isDeletingGivebutterDomain, setIsDeletingGivebutterDomain] = useState(false);

    const { data: engageDomains } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageDomains(accountId),
        queryFn: () => getEngageDomains(accountId),
        ...transformEngageDomainQuery(),
    });

    const hasCustomDomain = engageDomains?.customDomain !== undefined;
    const hasGivebutterDomain = engageDomains?.givebutterDomain !== undefined;
    const hasAtleastOneDomain = hasCustomDomain || hasGivebutterDomain;
    const hasBothDomains = hasCustomDomain && hasGivebutterDomain;

    return (
        <div className="mb-4 rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-b-gray-200 px-6 py-4">
                <span className="text-lg font-medium">Domains</span>
                {hasAtleastOneDomain && !hasBothDomains && (
                    <Link to={`/accounts/${accountId}/engage/settings/domains/new`}>
                        <Button size="sm" variant="outline" color="neutral">
                            <FontAwesomeIcon icon={faPlus} />
                            Add New
                        </Button>
                    </Link>
                )}
            </div>
            <div className="flex flex-col gap-4 p-4">
                {!hasAtleastOneDomain && (
                    <div className="flex flex-col items-center justify-center text-sm font-medium text-gray-600">
                        <FontAwesomeIcon
                            icon={faLink}
                            className="mb-2 mt-8 rounded-full bg-gray-100 p-3"
                        />
                        No domain added
                        <Link to={`/accounts/${accountId}/engage/settings/domains/new`}>
                            <Button variant="solid" size="sm" className="mb-8 mt-3">
                                <FontAwesomeIcon icon={faPlus} />
                                Add Domain
                            </Button>
                        </Link>
                    </div>
                )}

                {engageDomains?.customDomain && (
                    <div className="rounded border border-gray-200 p-4">
                        <div className="mb-6">
                            <div className="flex items-center justify-between font-medium">
                                <div>
                                    <FontAwesomeIcon
                                        icon={faGlobe}
                                        className="mr-2 text-gray-400"
                                    />
                                    <span className="mr-2">{engageDomains.customDomain.value}</span>
                                    {engageDomains.customDomainHasUnverifiedRecords ? (
                                        <span className="text-xs text-red-500">
                                            <FontAwesomeIcon icon={faExclamationTriangle} /> Not
                                            connected
                                        </span>
                                    ) : (
                                        <span className="text-xs text-green-500">
                                            <FontAwesomeIcon icon={faCheck} /> Connected
                                        </span>
                                    )}
                                </div>
                                <DropdownMenu.Root>
                                    <DropdownMenu.EllipsisTrigger className="px-2" />
                                    <DropdownMenu.Content>
                                        <DropdownMenu.LinkItem
                                            to={`/accounts/${accountId}/engage/settings/domains/${engageDomains.customDomain.id}/edit`}
                                        >
                                            Edit
                                        </DropdownMenu.LinkItem>
                                        <DropdownMenu.Item
                                            onClick={() => {
                                                setIsDeletingCustomDomain(true);
                                            }}
                                        >
                                            <div className="text-red-500">
                                                <FontAwesomeIcon icon={faTrash} className="mr-2" />
                                                Delete
                                            </div>
                                        </DropdownMenu.Item>
                                    </DropdownMenu.Content>
                                </DropdownMenu.Root>

                                <DeleteDomainDialog
                                    domain={engageDomains.customDomain}
                                    open={isDeletingCustomDomain}
                                    onOpenChange={setIsDeletingCustomDomain}
                                />
                            </div>
                            {!!engageDomains.customDomain.reply_to && (
                                <div className="ml-6 text-xs italic text-gray-500">
                                    Reply-to: {engageDomains.customDomain.reply_to}
                                </div>
                            )}
                        </div>
                        <div className="mb-3 text-sm font-medium">DNS Records</div>
                        <DNSRecordTable domain={engageDomains.customDomain} inPortal={false} />
                        <DNSVerification domain={engageDomains.customDomain} />
                    </div>
                )}

                {engageDomains?.givebutterDomain && (
                    <div className="rounded border border-gray-200 p-4">
                        <div className="mb-6">
                            <div className="flex w-full items-center justify-between font-medium">
                                <div>
                                    <FontAwesomeIcon
                                        icon={faGlobe}
                                        className="mr-2 text-gray-400"
                                    />
                                    <span className="mr-2">
                                        {engageDomains.givebutterDomain.value}
                                    </span>
                                    {engageDomains.givebutterDomain.status !== 'active' ? (
                                        <Tooltip.Root>
                                            <Tooltip.Trigger>
                                                <span className="text-xs text-orange-500">
                                                    <FontAwesomeIcon icon={faCircleInfo} /> Setting
                                                    up
                                                </span>
                                            </Tooltip.Trigger>
                                            <Tooltip.Portal>
                                                <Tooltip.Content inverse>
                                                    <div>This may take up to 30 minutes</div>
                                                    <Tooltip.Arrow inverse />
                                                </Tooltip.Content>
                                            </Tooltip.Portal>
                                        </Tooltip.Root>
                                    ) : (
                                        <span className="text-xs text-green-500">
                                            <FontAwesomeIcon icon={faCheck} /> Connected
                                        </span>
                                    )}
                                </div>
                                <DropdownMenu.Root>
                                    <DropdownMenu.EllipsisTrigger className="px-2" />
                                    <DropdownMenu.Content>
                                        <DropdownMenu.LinkItem
                                            to={`/accounts/${accountId}/engage/settings/domains/${engageDomains.givebutterDomain.id}/edit`}
                                        >
                                            Edit
                                        </DropdownMenu.LinkItem>
                                        <DropdownMenu.Item
                                            onClick={() => {
                                                setIsDeletingGivebutterDomain(true);
                                            }}
                                        >
                                            <div className="text-red-500">
                                                <FontAwesomeIcon icon={faTrash} className="mr-2" />
                                                Delete
                                            </div>
                                        </DropdownMenu.Item>
                                    </DropdownMenu.Content>
                                </DropdownMenu.Root>

                                <DeleteDomainDialog
                                    domain={engageDomains.givebutterDomain}
                                    open={isDeletingGivebutterDomain}
                                    onOpenChange={setIsDeletingGivebutterDomain}
                                />
                            </div>
                            {!!engageDomains.givebutterDomain.reply_to && (
                                <div className="ml-6 text-xs italic text-gray-500">
                                    Reply-to: {engageDomains.givebutterDomain.reply_to}
                                </div>
                            )}
                        </div>
                        <div className="mt-4 w-full">
                            <DNSVerification domain={engageDomains.givebutterDomain} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DomainSettings;
